// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UnifiedAuthStorage } from '../services/unified-auth.service';
import ThemeService from '../services/theme.service';
import { toast } from './ui/toast';
import { logger } from '@/shared/observability/logger';


export type Theme = 'light' | 'dark' | 'system' | 'neomorphism' | 'neomorphism-dark' | 'light-glass' | 'dark-glass';
export type ThemeCategory = 'default' | 'glass' | 'neomorphism';
export type Plan = 'FREE' | 'PRO' | 'ENTERPRISE' | 'BUSINESS';

export const THEME_CATEGORIES: Record<ThemeCategory, Theme[]> = {
  default: ['light', 'dark', 'system'],
  glass: ['light-glass', 'dark-glass'],
  neomorphism: ['neomorphism', 'neomorphism-dark'],
};

const THEME_SELECTION_PREFIX = 'xtremand-theme-';

const getThemeStorageKey = (category: ThemeCategory) => `${THEME_SELECTION_PREFIX}${category}`;

const getStoredThemeForCategory = (category: ThemeCategory): Theme | null => {
  if (typeof window === 'undefined') return null;
  const storedTheme = localStorage.getItem(getThemeStorageKey(category));
  if (!storedTheme) return null;
  const normalized = normalizeTheme(storedTheme);
  if (!normalized) return null;
  if (!THEME_CATEGORIES[category].includes(normalized)) return null;
  if (!ThemeService.isValidTheme(normalized)) return null;
  if (!canUseTheme(getCurrentUserPlan(), normalized)) return null;
  return normalized;
};
const setStoredThemeForCategory = (category: ThemeCategory, theme: Theme) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(getThemeStorageKey(category), theme);
};
const THEME_LABELS: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
  'light-glass': 'Light Glass',
  'dark-glass': 'Dark Glass',
  neomorphism: 'Neomorphism',
  'neomorphism-dark': 'Neomorphism Dark',
};
export const THEME_CATEGORY_STORAGE_KEY = 'xtremand-theme-category';
export const DEFAULT_THEME_CATEGORY: ThemeCategory = 'default';

const RESTRICTED_THEMES: Theme[] = ['light-glass', 'dark-glass', 'neomorphism', 'neomorphism-dark'];
const DEFAULT_THEME: Theme = 'light';

export function canUseTheme(plan: Plan | undefined, theme: string, category?: ThemeCategory): boolean {
  const normalizedPlan = (plan || 'FREE').toString().toUpperCase();
  if (theme === 'system' && category && category !== 'default') {
    return false;
  }

  const isRestricted = RESTRICTED_THEMES.includes(theme as Theme);
  if (!isRestricted) return true;
  return normalizedPlan === 'PRO' || normalizedPlan === 'ENTERPRISE' || normalizedPlan === 'BUSINESS';
}
export function canUseThemeCategory(plan: Plan | undefined, category: ThemeCategory): boolean {
  const normalizedPlan = (plan || 'FREE').toString().toUpperCase();
  if (category === 'default') return true;
  return normalizedPlan === 'PRO' || normalizedPlan === 'ENTERPRISE' || normalizedPlan === 'BUSINESS';
}

export const getCurrentUserPlan = (): Plan => {
  const user = UnifiedAuthStorage.getUser();
  
  // Try planDisplayName first (from API)
  const display = user?.planDisplayName?.toString().toUpperCase() || '';
  if (display.includes('FREE')) return 'FREE';
  if (display.includes('PRO')) return 'PRO';
  if (display.includes('ENTERPRISE')) return 'ENTERPRISE';
  if (display.includes('BUSINESS')) return 'BUSINESS';
  
  // Fallback to plan field
  const plan = user?.plan?.toString().toUpperCase();
  if (plan === 'PRO' || plan === 'ENTERPRISE' || plan === 'BUSINESS' || plan === 'FREE') {
    return plan as Plan;
  }
  
  return 'FREE';
};

const normalizeTheme = (theme?: string | null): Theme | null => {
  if (!theme || typeof theme !== 'string') return null;
  const canonical = theme.trim().toLowerCase();
  if (canonical === 'neumorphism-light' || canonical === 'neomorphism')  return 'neomorphism';
  if (canonical === 'neumorphism-dark' || canonical === 'neomorphism-dark') return 'neomorphism-dark';
  if (canonical === 'light' || canonical === 'dark' || canonical === 'system' || canonical === 'light-glass' || canonical === 'dark-glass') {
    return canonical as Theme;
  }
  return null;
};

export const isValidThemeCategory = (value: string | null): value is ThemeCategory => {
  return value === 'default' || value === 'glass' || value === 'neomorphism';
};

export const categorizeTheme = (theme: Theme): ThemeCategory => {
  if (theme === 'light' || theme === 'dark' || theme === 'system') return 'default';
  if (theme === 'light-glass' || theme === 'dark-glass') return 'glass';
  if (theme === 'neomorphism' || theme === 'neomorphism-dark') return 'neomorphism';
  return 'default';
};

export const getThemeCategory = (theme: Theme | null | undefined): ThemeCategory => {
  const normalized = normalizeTheme(theme);
  if (!normalized) return DEFAULT_THEME_CATEGORY;
  return categorizeTheme(normalized);
};

const getStoredThemeCategory = (): ThemeCategory | null => {
  if (typeof window === 'undefined') return null;
  const storedCategory = localStorage.getItem(THEME_CATEGORY_STORAGE_KEY);
  if (storedCategory && isValidThemeCategory(storedCategory) && canUseThemeCategory(getCurrentUserPlan(), storedCategory)) {
    return storedCategory;
  }
  return null;
};

const getCategoryForSystem = (currentCategory: ThemeCategory | null): ThemeCategory => {
  if (currentCategory && canUseThemeCategory(getCurrentUserPlan(), currentCategory)) {
    return currentCategory;
  }
  const storedCategory = getStoredThemeCategory();
  if (storedCategory) {
    return storedCategory;
  }
  return DEFAULT_THEME_CATEGORY;
};

const getBackendTheme = (): Theme | null => {
  const userInfoTheme = UnifiedAuthStorage.getUserInfo()?.theme;
  const userTheme = UnifiedAuthStorage.getUser()?.theme;
  return normalizeTheme(userInfoTheme) || normalizeTheme(userTheme);
};

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const plan = getCurrentUserPlan();
  const stored = localStorage.getItem('xtremand-theme') as Theme | null;
  if (stored && ThemeService.isValidTheme(stored) && canUseTheme(plan, stored)) {
    return stored;
  }
  if (stored && !canUseTheme(plan, stored)) {
    localStorage.setItem('xtremand-theme', DEFAULT_THEME);
    return DEFAULT_THEME;
  }
  const backendTheme = getBackendTheme();
  if (backendTheme && ThemeService.isValidTheme(backendTheme) && canUseTheme(plan, backendTheme)) {
    localStorage.setItem('xtremand-theme', backendTheme);
    return backendTheme;
  }
  localStorage.setItem('xtremand-theme', DEFAULT_THEME);
  return DEFAULT_THEME;
};

type ActualTheme = 'light' | 'dark' | 'neomorphism' | 'neomorphism-dark' | 'light-glass' | 'dark-glass';
const BACKEND_SYNC_COOLDOWN_MS = 5000;

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme, category?: ThemeCategory, options?: { silent?: boolean }) => void;
  actualTheme: ActualTheme;
  themeCategory: ThemeCategory;
  setThemeCategory: (category: ThemeCategory) => void;
  getAvailableThemes: () => Theme[];
  getSelectedThemeForCategory: (category: ThemeCategory) => Theme | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: {readonly children: React.ReactNode }) {
  const [themeByCategory, setThemeByCategory] = useState<Record<ThemeCategory, Theme | null> >(() => {
    if (typeof globalThis !== 'undefined') {
     return { default: null, glass: null, neomorphism: null };
    }
    const initialCache: Record<ThemeCategory, Theme | null> = {
      default: getStoredThemeForCategory('default'),
      glass: getStoredThemeForCategory('glass'),
      neomorphism: getStoredThemeForCategory('neomorphism'),
    };
    const fallbackTheme = getInitialTheme();
    const fallbackCategory = getStoredThemeCategory() ?? getThemeCategory(fallbackTheme);
    if (!initialCache[fallbackCategory]) {
      const normalized = normalizeTheme(fallbackTheme);
      if (normalized && getThemeCategory(normalized) === fallbackCategory) {
        initialCache[fallbackCategory] = normalized;
        setStoredThemeForCategory(fallbackCategory, normalized);
      }
    }
    return initialCache;
  });

  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME;
    const initialTheme = getInitialTheme();
    const initialCategory = getStoredThemeCategory() ?? getThemeCategory(initialTheme);
    const saved = getStoredThemeForCategory(initialCategory);
    if (saved) return saved;
    return initialTheme;
  });
  const [actualTheme, setActualTheme] = useState<ActualTheme>('light');

  const [currentThemeCategory, setCurrentThemeCategory] = useState<ThemeCategory>(() => {
    if (typeof window !== 'undefined') {
      const initialTheme = getInitialTheme();
      const initialCategory = getThemeCategory(initialTheme);
      localStorage.setItem(THEME_CATEGORY_STORAGE_KEY, initialCategory);
      return initialCategory;
    }
    return DEFAULT_THEME_CATEGORY;
  });
  const [themeCategorySource, setThemeCategorySource] = useState<'inferred' | 'user'>('inferred');
  const lastUserActionAtRef = useRef<number>(0);
  const lastRequestedThemeRef = useRef<Theme | null>(null);
  useEffect(() => {
    lastRequestedThemeRef.current = theme;
  }, [theme]);

  const getSystemTheme = (): 'light' | 'dark' => {
    if (typeof globalThis !== 'undefined') {
      return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  };

  const resolveTheme = (currentTheme: Theme, category: ThemeCategory = currentThemeCategory): ActualTheme => {
    if (currentTheme === 'neomorphism') return 'neomorphism';
    if (currentTheme === 'neomorphism-dark') return 'neomorphism-dark';
    if (currentTheme === 'light-glass' || currentTheme === 'dark-glass') return currentTheme;
    if (currentTheme === 'system') return getSystemTheme() as ActualTheme;
    return currentTheme;
  };

  const applyThemeToDOM = (resolvedTheme: ActualTheme) => {
    
    if (typeof globalThis !== 'undefined')   {
      const root = globalThis.document.documentElement;
      root.classList.remove('light', 'dark', 'neomorphism', 'neomorphism-dark','theme-light-glass', 'theme-dark-glass');
      const classToAdd = (resolvedTheme === 'light-glass' || resolvedTheme === 'dark-glass') 
        ? `theme-${resolvedTheme}` 
        : resolvedTheme;
      root.classList.add(classToAdd);
      
      if (resolvedTheme === 'neomorphism') {
        root.classList.remove('dark');
        root.style.colorScheme = 'light';
        root.setAttribute('data-theme', 'neumorphic');
      } else if (resolvedTheme === 'neomorphism-dark') {
          root.classList.add('dark');
          root.style.colorScheme = 'dark';
          root.setAttribute('data-theme', 'neumorphic');
      } else if (resolvedTheme === 'light-glass' || resolvedTheme === 'dark-glass') {
        root.style.colorScheme = resolvedTheme === 'light-glass' ? 'light' : 'dark';
        root.removeAttribute('data-theme');
      }
      else {
        root.style.colorScheme = resolvedTheme;
        root.removeAttribute('data-theme');
      }
    }
  };

  const updateActualTheme = (currentTheme: Theme) => {
    const resolvedTheme = resolveTheme(currentTheme, currentThemeCategory);
    setActualTheme(resolvedTheme);
    applyThemeToDOM(resolvedTheme);
  };

  const setThemeCategoryInternal = (category: ThemeCategory, source: 'inferred' | 'user') => {
    const plan = getCurrentUserPlan();
    if (!canUseThemeCategory(plan, category)) {
      const fallback = DEFAULT_THEME_CATEGORY;
      setCurrentThemeCategory(fallback);
      setThemeCategorySource('inferred');
    if (typeof globalThis !== 'undefined') {
      localStorage.setItem(THEME_CATEGORY_STORAGE_KEY, fallback);
      }
      return;
    }
    setCurrentThemeCategory(category);
    setThemeCategorySource(source);
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_CATEGORY_STORAGE_KEY, category);
    }
  };
  const setThemeCategory = (category: ThemeCategory) => {
    setThemeCategoryInternal(category, 'user');
  };

  const getSelectedThemeForCategory = (category: ThemeCategory): Theme | null => {
    const selected = themeByCategory[category];
    return selected || null;
  };
  
  const getAvailableThemes = (): Theme[] => {
    const plan = getCurrentUserPlan();
    const activeCategory = canUseThemeCategory(plan, currentThemeCategory) ? currentThemeCategory : DEFAULT_THEME_CATEGORY;
    return THEME_CATEGORIES[activeCategory].filter((themeOption) => canUseTheme(plan, themeOption));
  };
  const updateTheme = (newTheme: Theme, overrideCategory?: ThemeCategory, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    const user = UnifiedAuthStorage.getUser();
    const plan = getCurrentUserPlan();
    if (newTheme === theme || newTheme === lastRequestedThemeRef.current) {
      return;
    }
    lastRequestedThemeRef.current = newTheme;
    // Validate theme access control
    if (!canUseTheme(plan, newTheme)) {
      const fallbackTheme: Theme = DEFAULT_THEME;
      setTheme(fallbackTheme);
      if (typeof window !== 'undefined') {
        localStorage.setItem('xtremand-theme', fallbackTheme);
      }
      updateActualTheme(fallbackTheme);
      logger.warn(`⚠️ Theme "${newTheme}" not available for plan "${plan}". Falling back to "${fallbackTheme}"`);
      return;
    }

    const resolveCategory = overrideCategory ?? currentThemeCategory;
    const themeToStore = newTheme;

    if (!THEME_CATEGORIES[resolveCategory].includes(themeToStore)) {
      logger.warn(`⚠️ Theme "${themeToStore}" is not valid for category "${resolveCategory}"`);
      if (!silent) toast.info('Selected theme is unavailable in the current category.');
      return;
    }
    lastUserActionAtRef.current = Date.now();
    setThemeByCategory((prev) => {
      const updated = { ...prev, [resolveCategory]: themeToStore };
      return updated;
    });
    setTheme(themeToStore);
    updateActualTheme(themeToStore);

    // Silent updates (e.g. forcing a default on mount) only affect the
    // rendered state — they don't persist as a chosen preference or sync
    // to the backend, and don't announce themselves via toast.
    if (!silent) {
      setStoredThemeForCategory(resolveCategory, themeToStore);
      if (typeof window !== 'undefined') {
        localStorage.setItem('xtremand-theme', themeToStore);
      }
      const themeName = THEME_LABELS[themeToStore] || themeToStore;
      toast.success(`${themeName} theme applied`);
    }

    const derivedCategory = themeToStore === 'system'
      ? resolveCategory
      : getThemeCategory(themeToStore);
    setThemeCategoryInternal(derivedCategory, 'inferred');
    logger.debug(`✨ Theme changed locally: "${themeToStore}" (resolved to "${resolveTheme(themeToStore, resolveCategory)}")`);

    if (!silent) {
      const userIdToSend = user?.id || user?.userId;
      if (userIdToSend) {
        logger.debug(`🛰️ Syncing theme to backend: userId=${userIdToSend}, theme=${themeToStore}`);
        ThemeService.updateTheme(userIdToSend, themeToStore)
          .then(response => {
            if (response.success) {
              logger.debug(`✅ Theme synced to backend: "${newTheme}"`);
            } else {
              logger.warn(`⚠️ Backend theme sync failed: ${response.error}`);
            }
          })
          .catch(error => {
            logger.error('❌ Error syncing theme to backend:', error);
          });
      } else {
        logger.warn('⚠️ No user ID found, theme change will not be synced to backend');
      }
    }
  };

  useEffect(() => {
    if (typeof globalThis !== 'undefined') {
      const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        if (theme === 'system') {
          updateActualTheme('system');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  useEffect(() => {
    const derivedCategory = getThemeCategory(theme);
    if (derivedCategory !== currentThemeCategory && themeCategorySource === 'inferred') {
      setThemeCategoryInternal(derivedCategory, 'inferred');
    }
  }, [theme, currentThemeCategory, themeCategorySource]);

  // CRITICAL: Monitor for auth changes and sync theme from backend on login
  const resetToSystemTheme = () => {
    setThemeByCategory({ default: 'system', glass: null, neomorphism: null });
    if (typeof window !== 'undefined') {
      localStorage.removeItem(getThemeStorageKey('glass'));
      localStorage.removeItem(getThemeStorageKey('neomorphism'));
      setStoredThemeForCategory('default', 'system');
      localStorage.setItem('xtremand-theme', 'system');
    }
    setTheme('system');
    setThemeCategoryInternal('default', 'inferred');
    updateActualTheme('system');
    logger.debug('🔓 Theme reset to system after logout');
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleLogout = () => {
      logger.debug('🔔 [ThemeProvider] Logout detected, resetting theme to system');
      resetToSystemTheme();
    };
    window.addEventListener('xtremand-logout', handleLogout);
    return () => window.removeEventListener('xtremand-logout', handleLogout);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const getSafeBackendTheme = (): Theme | null => {
      const backendTheme = getBackendTheme(); 
      const plan = getCurrentUserPlan(); 
      if (backendTheme && ThemeService.isValidTheme(backendTheme) && canUseTheme(plan, backendTheme)) { 
        return backendTheme;
      }
      return null;
    };
    const syncThemeFromBackend = () => {
      const now = Date.now();
      if (now - lastUserActionAtRef.current < BACKEND_SYNC_COOLDOWN_MS) {
        logger.debug('⏳ Skipping backend theme sync: recent manual theme update detected');
        return;

      }
      const backendTheme = getSafeBackendTheme();
      const storedTheme = localStorage.getItem('xtremand-theme') as Theme | null;
      const fallbackTheme = storedTheme && ThemeService.isValidTheme(storedTheme) && canUseTheme(getCurrentUserPlan(), storedTheme)
        ? storedTheme
        : DEFAULT_THEME;
      const resolvedTheme = backendTheme ?? fallbackTheme;

      // Applied unconditionally (not gated on resolvedTheme !== storedTheme).
      // The public login/signup page silently forces dark on mount without
      // persisting it, so the in-memory theme can read "dark" here even when
      // localStorage already holds the correct value — comparing against
      // localStorage alone would skip the setTheme call and leave the silent
      // override in place.
      setTheme(resolvedTheme);
      if (resolvedTheme !== storedTheme) {
        localStorage.setItem('xtremand-theme', resolvedTheme);
      }
      const derivedCategory = resolvedTheme === 'system' ? getCategoryForSystem(currentThemeCategory) : getThemeCategory(resolvedTheme);
      setThemeCategoryInternal(derivedCategory, 'inferred');
      logger.debug(`🔐 Theme resolved after login: "${resolvedTheme}" (backend: ${backendTheme ?? 'none'})`);
    };

    const handleAuthUpdate = () => {
      logger.debug('🔔 [ThemeProvider] Auth update detected, syncing backend theme...');
      syncThemeFromBackend();
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'xtremand-theme') return;
      if (!e.newValue) return;
      const incomingTheme = normalizeTheme(e.newValue);
      if (!incomingTheme) return;
      const now = Date.now();
      if (now - lastUserActionAtRef.current < BACKEND_SYNC_COOLDOWN_MS) {
        logger.debug('⏳ Skipping storage theme sync: recent manual theme update detected');
        return;
      }
      if (ThemeService.isValidTheme(incomingTheme) && canUseTheme(getCurrentUserPlan(), incomingTheme)) {
        const possibleCurrent = localStorage.getItem('xtremand-theme') as Theme | null;
        if (incomingTheme !== possibleCurrent) {
          setTheme(incomingTheme);
          const derivedCategory = incomingTheme === 'system' ? getCategoryForSystem(currentThemeCategory) : getThemeCategory(incomingTheme);
          setThemeCategoryInternal(derivedCategory, 'inferred');
          logger.debug(`📄 Cross-tab theme sync applied: "${incomingTheme}"`);
        }
      }
    };
    window.addEventListener('xtremand-auth-updated', handleAuthUpdate);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('xtremand-auth-updated', handleAuthUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (typeof globalThis !== 'undefined') {
       updateActualTheme(theme);
    }
  }, [theme, currentThemeCategory]);

  const value = useMemo(() => ({
    theme,
    setTheme: updateTheme,
    actualTheme,
    themeCategory: currentThemeCategory,
    setThemeCategory,
    getAvailableThemes,
    getSelectedThemeForCategory,
  }), [theme, actualTheme, currentThemeCategory]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
